import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock } from 'lucide-react';

/**
 * Session inactivity guard.
 * Shows a warning modal N seconds before the session expires; on countdown
 * end, forces logout. Any user activity (mousemove, keypress, click, scroll)
 * resets the timer. Only active when a user is authenticated.
 *
 * Defaults tuned for enterprise SaaS: 30 min timeout, 60 s warning.
 */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_MS = 60 * 1000;           // 60 seconds warning

export function SessionTimeoutGuard() {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [remaining, setRemaining] = useState(WARNING_MS / 1000);
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const clearAllTimers = () => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (logoutTimerRef.current) { clearTimeout(logoutTimerRef.current); logoutTimerRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  };

  const startTimers = () => {
    clearAllTimers();
    warnTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemaining(WARNING_MS / 1000);
      countdownRef.current = setInterval(() => {
        setRemaining((r) => (r > 0 ? r - 1 : 0));
      }, 1000);
      logoutTimerRef.current = setTimeout(() => {
        logout();
      }, WARNING_MS);
    }, IDLE_TIMEOUT_MS - WARNING_MS);
  };

  const resetTimers = () => {
    if (showWarning) return; // don't reset while dialog is open
    startTimers();
  };

  useEffect(() => {
    if (!user) { clearAllTimers(); setShowWarning(false); return; }
    startTimers();
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(ev => window.addEventListener(ev, resetTimers, { passive: true }));
    return () => {
      clearAllTimers();
      events.forEach(ev => window.removeEventListener(ev, resetTimers));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, showWarning]);

  const stayActive = () => {
    setShowWarning(false);
    startTimers();
  };

  if (!user) return null;

  return (
    <Dialog open={showWarning} onOpenChange={(o) => { if (!o) stayActive(); }}>
      <DialogContent data-testid="session-timeout-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-600" />
            Su sesión está por expirar
          </DialogTitle>
          <DialogDescription>
            Por seguridad, su sesión se cerrará automáticamente por inactividad.
            <span className="block mt-2 text-2xl font-mono font-semibold text-slate-900" data-testid="session-countdown">
              {remaining}s
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={logout} data-testid="session-logout-button">
            Cerrar sesión
          </Button>
          <Button onClick={stayActive} data-testid="session-stay-button">
            Continuar trabajando
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
